import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  useTheme,
  Tabs,
  Tab,
  IconButton,
  Drawer,
  AppBar,
  Toolbar,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { 
  Search as SearchIcon, 
  ShoppingCart as CartIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  Receipt as ReceiptIcon,
  FileDownload as DownloadIcon,
  History as HistoryIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  List as ListIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import productService from '../../services/productService';
import salesService from '../../services/salesService';
import POSProductList from './POSProductList';
import POSCart from './POSCart';
import POSPayment from './POSPayment';
import CustomerSelector from './CustomerSelector';
import creditService from '../../services/creditService';
import categoryService from '../../services/categoryService';
import HoldBillModal from './HoldBillModal';
import ViewHoldBillsModal from './ViewHoldBillsModal';
import { holdBillService } from '../../services/holdBillService';

// Styled components
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  position: 'relative',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  boxShadow: theme.shadows[1],
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

// Styled customer button
const CustomerButton = styled(Button)(({ theme }) => ({
  background: '#FF5722', // Bright orange color to stand out
  color: 'white',
  fontWeight: 'bold',
  padding: '10px 16px',
  borderRadius: '8px',
  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
  '&:hover': {
    background: '#E64A19',
    boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
  },
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  transition: 'all 0.3s ease'
}));

/**
 * POS (Point of Sale) main component
 * @returns {JSX.Element} POS screen
 */
const POS = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [rootCategories, setRootCategories] = useState([]);
  const [subcategories, setSubcategories] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(0); // 0 = All
  const [selectedParentCategory, setSelectedParentCategory] = useState(0); // 0 = All, used to track parent category
  const [displayedCategories, setDisplayedCategories] = useState([]); // Categories to be displayed in tabs
  const [cart, setCart] = useState(() => {
    // Initialize cart from localStorage if available
    const savedCart = localStorage.getItem('posCart');
    return savedCart ? JSON.parse(savedCart) : [];
  });
  const [cartOpen, setCartOpen] = useState(false);
  const [customer, setCustomer] = useState(() => {
    // Initialize customer from localStorage if available
    const savedCustomer = localStorage.getItem('posCustomer');
    return savedCustomer ? JSON.parse(savedCustomer) : null;
  });
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [currentSale, setCurrentSale] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [recentTransactionsOpen, setRecentTransactionsOpen] = useState(false);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [holdBillModalOpen, setHoldBillModalOpen] = useState(false);
  const [viewHoldBillsModalOpen, setViewHoldBillsModalOpen] = useState(false);
  
  // Discount and Additional Charges state
  const [discount, setDiscount] = useState(0);
  const [additionalCharges, setAdditionalCharges] = useState(0);
  
  // Cart panel width
  const cartWidth = 420; // Increased from 350
  
  // Mobile responsive state
  const isMobile = window.innerWidth < 960;

  // Handle navigation back to dashboard
  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  // Handle sale error
  const handleSaleError = (error) => {
    console.error('Sale error:', error);
    const errorMessage = error.response?.data?.message || error.message || 'Failed to complete sale';
    setErrorMessage(errorMessage);
    enqueueSnackbar(errorMessage, { variant: 'error' });
  };

  // Helper function to safely get customer credit balance
  const getCustomerCreditBalance = (customer) => {
    if (!customer) return '0.00';
    
    // Try different possible field names
    const creditValue = customer.creditBalance || customer.credit_balance || customer.credit || 0;
    return Number(creditValue).toFixed(2);
  };

  // Load products and categories on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [productsData, categoriesData] = await Promise.all([
          productService.getAllProducts(0, 5000), // Increased size to fetch more products
          categoryService.getAllCategories({ page: 0, size: 1000 })
        ]);
        
        // Filter out inactive products immediately after fetch
        const activeProducts = (productsData.content || []).filter(product => 
          String(product.status || 'ACTIVE').toUpperCase() === 'ACTIVE'
        );

        setProducts(activeProducts);
        setFilteredProducts(activeProducts); // Initialize filtered products with active ones
        
        // Process category hierarchy
        const categoryData = categoriesData.content || categoriesData;
        const { rootCategories: roots, subcategories: subs } = categoryService.buildCategoryTree({ content: categoryData });
        
        // Set root categories and subcategories
        setRootCategories(roots);
        setSubcategories(subs);
        
        // Display root categories with "All" option
        setDisplayedCategories([
          { id: 0, name: 'ALL' },
          ...roots
        ]);
      } catch (error) {
        console.error('Error loading POS data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Filter products by category and search query
  useEffect(() => {
    let filtered = [...products].filter(product => 
      // First filter out inactive products
      String(product.status || 'ACTIVE').toUpperCase() === 'ACTIVE'
    );
    
    // Then filter by category
    if (selectedCategory !== 0) {
      // Direct category filter - when user selects a specific category
      filtered = filtered.filter(product => 
        product.category && product.category.id === selectedCategory
      );
    } else if (selectedParentCategory !== 0) {
      // Parent category filter - when user is viewing "ALL IN CATEGORY"
      // Show all products that belong to the parent category or any of its subcategories
      const subcategoryIds = subcategories[selectedParentCategory]?.map(sub => sub.id) || [];
      filtered = filtered.filter(product => 
        (product.category && product.category.id === selectedParentCategory) || // Products directly in parent
        (product.category && subcategoryIds.includes(product.category.id)) // Products in subcategories
      );
    }
    
    // Finally filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(query) ||
        (product.barcode && product.barcode.toLowerCase().includes(query)) ||
        (product.sku && product.sku.toLowerCase().includes(query)) ||
        (product.description && product.description.toLowerCase().includes(query))
      );
    }
    
    setFilteredProducts(filtered);
  }, [products, selectedCategory, selectedParentCategory, searchQuery, subcategories]);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Handle category tab change
  const handleCategoryChange = (event, newValue) => {
    const newCategory = displayedCategories.find(cat => cat.id === newValue);
    
    if (newCategory) {
      // Set the selected category
      setSelectedCategory(newValue);
      
      // If it's the All category
      if (newValue === 0) {
        // If we're inside a parent category view
        if (selectedParentCategory !== 0) {
          // Keep the parent category context - just show all products in this category
          // The filtering will be handled in the useEffect
        } else {
          // Reset to root categories when top-level "All" is selected
          setDisplayedCategories([
            { id: 0, name: 'ALL' },
            ...rootCategories
          ]);
          setSelectedParentCategory(0);
        }
      } else {
        // Check if this category has subcategories
        const categoryHasSubcategories = subcategories[newValue] && subcategories[newValue].length > 0;
        
        if (categoryHasSubcategories) {
          // Show the subcategories of this parent
          setDisplayedCategories([
            { id: 0, name: 'ALL IN CATEGORY' },
            ...subcategories[newValue]
          ]);
          setSelectedParentCategory(newValue);
          setSelectedCategory(0); // Select "ALL IN CATEGORY" by default
        }
      }
    }
  };

  // Handle back to parent categories
  const handleBackToParentCategories = () => {
    setDisplayedCategories([
      { id: 0, name: 'ALL' },
      ...rootCategories
    ]);
    setSelectedParentCategory(0);
    setSelectedCategory(0);
  };

  // Add product to cart
  const handleAddToCart = (product) => {
    const existingItem = cart.find(item => item.product.id === product.id);
    
    // Map backend properties to frontend
    const price = product.sellingPrice || product.salePrice || 0;
    
    if (existingItem) {
      const updatedCart = cart.map(item =>
        item.product.id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      );
      setCart(updatedCart);
    } else {
      setCart([...cart, { 
        product, 
        quantity: 1, 
        price: price,
        tax: product.tax || null
      }]);
    }
  };

  // Remove product from cart
  const handleRemoveFromCart = (productId) => {
    const updatedCart = cart.filter(item => item.product.id !== productId);
    setCart(updatedCart);
  };

  // Update product quantity in cart
  const handleUpdateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      handleRemoveFromCart(productId);
      return;
    }
    
    const updatedCart = cart.map(item =>
      item.product.id === productId
        ? { ...item, quantity }
        : item
    );
    setCart(updatedCart);
  };

  // Update product unit price in cart (with min price enforcement already handled by POSCart)
  const handleUpdatePrice = (productId, newPrice) => {
    const updatedCart = cart.map(item =>
      item.product.id === productId
        ? { ...item, price: newPrice }
        : item
    );
    setCart(updatedCart);
  };

  // Clear all items from cart
  const handleClearCart = () => {
    setCart([]);
    setDiscount(0);
    setAdditionalCharges(0);
  };

  // Calculate cart totals
  const calculateTotals = () => {
    const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const taxAmount = cart.reduce((sum, item) => {
      const taxRate = item.tax ? item.tax.rate / 100 : 0;
      return sum + (item.price * item.quantity * taxRate);
    }, 0);
    
    const discountAmount = Math.max(0, parseFloat(discount) || 0);
    const additionalChargesAmount = Math.max(0, parseFloat(additionalCharges) || 0);
    
    // Ensure discount doesn't exceed subtotal + tax
    const maxDiscount = subtotal + taxAmount;
    const finalDiscount = Math.min(discountAmount, maxDiscount);
    
    const total = Math.max(0, subtotal + taxAmount - finalDiscount + additionalChargesAmount);
    
    return {
      subtotal,
      taxAmount,
      discount: finalDiscount,
      additionalCharges: additionalChargesAmount,
      total
    };
  };

  // Handle opening the payment dialog
  const handleOpenPayment = () => {
    if (cart.length === 0) return;
    
    // Clear previous sale state to ensure fresh payment form
    setCurrentSale(null);
    setSuccessMessage(null);
    setErrorMessage(null);
    
    setPaymentDialogOpen(true);
  };

  // Handle completing the sale
  const handleCompleteSale = async (paymentData) => {
    try {
      setProcessingPayment(true);
      setErrorMessage(null);
      
      // Format the date
      const today = new Date();
      const formattedDate = today.toISOString().split('T')[0];
      
      // Check payment method types
      const isCredit = paymentData.method === 'CREDIT';
      const isImmediatePayment = ['CASH', 'CREDIT_CARD', 'PAYNOW'].includes(paymentData.method);
      const isPendingPayment = ['CREDIT', 'CHEQUE'].includes(paymentData.method);
      const totalAmount = calculateTotals().total;
      
      // Validate credit sale
      if (isCredit) {
        if (!customer || !customer.id) {
          // Set error message
          const errorMsg = "Customer selection is required for credit sales";
          setErrorMessage(errorMsg);
          enqueueSnackbar(errorMsg, { variant: 'error' });
          
          // Stop processing
          setProcessingPayment(false);
          return; // <-- Ensure we exit here
        }
      }
      
      // Determine payment status based on payment method
      const paymentStatus = isImmediatePayment ? "PAID" : (isPendingPayment ? "PENDING" : "PAID");
      console.log(`Payment method: ${paymentData.method}, Payment status: ${paymentStatus}`);
      
      // Prepare sales order data
      const salesOrder = {
        customerId: isCredit ? customer.id : (customer?.id || null),
        orderDate: formattedDate,
        companyId: JSON.parse(localStorage.getItem('user'))?.companyId || 1,
        items: cart.map(item => ({
          productId: item.product.id,
          quantity: item.quantity,
          unitPrice: item.price,
          taxId: item.tax ? item.tax.id : null,
          discount: 0
        })),
        paymentMethod: paymentData.method,
        paymentStatus: paymentStatus,
        status: "DELIVERED",
        notes: paymentData.notes || '',
        subtotal: calculateTotals().subtotal,
        taxAmount: calculateTotals().taxAmount,
        total: totalAmount,
        discount: calculateTotals().discount,
        shippingCost: calculateTotals().additionalCharges,
        updateCreditBalance: false
      };
      
      // Create sales order
      const result = await salesService.createSalesOrder(salesOrder);
      
      // NOTE: Credit balance is automatically handled by SalesOrderService
      // No need to create duplicate credit transactions here as it would cause double counting
      
      // Store current sale for receipt
      setCurrentSale(result);
      
      // Show success message
      setSuccessMessage(`Sale #${result.id || 'New'} completed successfully!`);
      enqueueSnackbar(`Sale completed successfully!`, { 
        variant: 'success',
        autoHideDuration: 3000
      });
      
      // Clear the cart and customer data from localStorage
      localStorage.removeItem('posCart');
      localStorage.removeItem('posCustomer');
      
      // Clear the state
      setCart([]);
      setCustomer(null);
      setDiscount(0);
      setAdditionalCharges(0);
      
    } catch (error) {
      console.error('Error completing sale:', error);
      handleSaleError(error);
    } finally {
      setProcessingPayment(false);
    }
  };

  // Handle printing receipt
  const handlePrintReceipt = async (receiptTypes = ['CUSTOMER']) => {
    if (!currentSale?.id) return;
    
    try {
      if (receiptTypes.length === 1) {
        // Single receipt type
        const type = receiptTypes[0];
        
        // Define custom header based on receipt type
        let customHeader;
        switch (type) {
          case 'CUSTOMER':
            customHeader = 'CUSTOMER COPY';
            break;
          case 'OFFICE':
            customHeader = 'OFFICE COPY';
            break;
          case 'OFFICE_WITH_SIGNATURE':
            customHeader = 'OFFICE COPY - CUSTOMER SIGNATURE REQUIRED';
            break;
          default:
            customHeader = null;
        }
        
        const receipt = await salesService.generateReceipt(currentSale.id, type, customHeader);
        
        // Create a blob URL and open it in a new tab
        const blob = new Blob([receipt], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        
        // Clean up the URL
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      } else {
        // Multiple receipt types
        const receipts = await salesService.generateMultipleReceipts(currentSale.id, receiptTypes);
        
        // Generate display names for receipt types
        const getReceiptName = (type) => {
          switch (type) {
            case 'CUSTOMER': return 'Customer';
            case 'OFFICE': return 'Office';
            case 'OFFICE_WITH_SIGNATURE': return 'Office_with_Signature';
            default: return type;
          }
        };
        
        // Open each receipt in a new tab
        receipts.forEach(({ type, blob }) => {
          const fileName = `Invoice-${currentSale.id}-${getReceiptName(type)}.pdf`;
          const url = window.URL.createObjectURL(blob);
          
          // Create a hidden anchor element for downloading
          const downloadLink = document.createElement('a');
          downloadLink.href = url;
          downloadLink.download = fileName;
          downloadLink.target = '_blank';
          
          // Add to DOM, click, then remove
          document.body.appendChild(downloadLink);
          
          // Open for viewing in new tab and trigger download
          window.open(url, '_blank');
          downloadLink.click();
          document.body.removeChild(downloadLink);
          
          // Clean up the URL
          setTimeout(() => window.URL.revokeObjectURL(url), 100);
        });
        
        enqueueSnackbar(`${receipts.length} invoices generated successfully`, { 
          variant: 'success',
          autoHideDuration: 3000
        });
      }
    } catch (error) {
      console.error('Error generating receipt(s):', error);
      enqueueSnackbar('Failed to generate receipt(s). Please try again.', {
        variant: 'error'
      });
    }
  };

  // Handle error close
  const handleErrorClose = () => {
    setErrorMessage(null);
  };

  // Handle success close
  const handleSuccessClose = () => {
    setSuccessMessage(null);
  };

  // Fetch recent transactions
  const fetchRecentTransactions = async () => {
    try {
      setLoadingTransactions(true);
      const response = await salesService.getAllSalesOrders({ page: 0, size: 3, sort: 'orderDate', direction: 'DESC' });
      setRecentTransactions(response.content || []);
    } catch (error) {
      console.error('Error fetching recent transactions:', error);
      enqueueSnackbar('Failed to load recent transactions', { variant: 'error' });
    } finally {
      setLoadingTransactions(false);
    }
  };

  // Handle opening recent transactions
  const handleOpenRecentTransactions = () => {
    setRecentTransactionsOpen(true);
    fetchRecentTransactions();
  };

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('posCart', JSON.stringify(cart));
  }, [cart]);

  // Save customer to localStorage whenever it changes
  useEffect(() => {
    if (customer) {
      localStorage.setItem('posCustomer', JSON.stringify(customer));
    } else {
      localStorage.removeItem('posCustomer');
    }
  }, [customer]);

  // Handle hold bill success
  const handleHoldBillSuccess = () => {
    enqueueSnackbar('Bill held successfully', { variant: 'success' });
    handleClearCart();
    setCustomer(null);
    localStorage.removeItem('posCustomer');
  };

  // Handle processing a hold bill
  const handleProcessHoldBill = async (bill) => {
    try {
      // Load items into cart
      setCart(bill.items.map(item => ({
        product: {
          id: item.productId,
          name: item.productName,
          salePrice: item.unitPrice
        },
        quantity: item.quantity,
        price: item.unitPrice,
        taxAmount: item.taxAmount
      })));

      // Set customer
      setCustomer({
        id: bill.customerId,
        name: bill.customerName
      });

      // Delete the hold bill since it's now in the cart
      await holdBillService.deleteHoldBill(bill.id);

      enqueueSnackbar('Hold bill loaded successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error processing hold bill:', error);
      enqueueSnackbar('Failed to process hold bill', { variant: 'error' });
    }
  };

  // Add a function to manually clear the draft
  const handleClearDraft = () => {
    localStorage.removeItem('posCart');
    localStorage.removeItem('posCustomer');
    setCart([]);
    setCustomer(null);
    setDiscount(0);
    setAdditionalCharges(0);
    enqueueSnackbar('Draft cleared successfully', { variant: 'success' });
  };

  return (
    <Box sx={{ 
      width: '100%', 
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column', 
      overflow: 'hidden'
    }}>
        {/* Top AppBar */}
        <StyledAppBar position="static" color="default" elevation={0}>
          <Toolbar>
          <IconButton 
            edge="start" 
            color="inherit" 
            onClick={handleBackToDashboard}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
            <Typography variant="h5" sx={{ flexGrow: 1, fontWeight: 600 }}>
              <ReceiptIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Point of Sale
            </Typography>
            
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                <TextField
                  placeholder="Search products..."
                  variant="outlined"
                  size="small"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  sx={{ width: 300, mr: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  variant="outlined"
                  startIcon={<HistoryIcon />}
                  onClick={handleOpenRecentTransactions}
                  sx={{ mr: 2 }}
                >
                  Recent Transactions
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ListIcon />}
                  onClick={() => setViewHoldBillsModalOpen(true)}
                  sx={{ mr: 2 }}
                >
                  View Hold Bills
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<SaveIcon />}
                  onClick={() => setHoldBillModalOpen(true)}
                  disabled={cart.length === 0}
                  sx={{ mr: 2 }}
                >
                  Hold Bill
                </Button>
                {(cart.length > 0 || customer) && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleClearDraft}
                    sx={{ mr: 2 }}
                  >
                    Clear Draft
                  </Button>
                )}
              </Box>
            )}
            
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mr: 2 }}>
            <CustomerButton
              startIcon={<PersonIcon sx={{ fontSize: 20 }} />}
                onClick={() => setCustomerDialogOpen(true)}
                fullWidth
              >
                {customer ? customer.name : 'Select Customer'}
            </CustomerButton>
              
              {customer && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'error.main', 
                    fontWeight: 'bold',
                    mt: 0.5
                  }}
                >
                  Due Balance: ${getCustomerCreditBalance(customer)}
                </Typography>
              )}
            </Box>
            
            <IconButton 
              color="primary" 
              onClick={() => setCartOpen(true)}
              sx={{ display: { xs: 'inline-flex', md: 'none' } }}
            >
              <Badge badgeContent={cart.length} color="error">
                <CartIcon />
              </Badge>
            </IconButton>
          </Toolbar>
        
        {/* Big Customer Select Button for better visibility - show on mobile and tablet */}
        {(isMobile) && (
          <Box sx={{ 
            p: 2, 
            display: { xs: 'block', md: 'none' },
            borderTop: 1,
            borderColor: 'divider' 
          }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
            <CustomerButton
              startIcon={<PersonIcon />}
              onClick={() => setCustomerDialogOpen(true)}
              fullWidth
              size="large"
            >
              {customer ? customer.name : 'Select Customer'}
            </CustomerButton>
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={4}>
                <Button
                  variant="outlined"
                  startIcon={<ListIcon />}
                  onClick={() => setViewHoldBillsModalOpen(true)}
                  fullWidth
                  size="large"
                >
                  View Hold Bills
                </Button>
              </Grid>
              <Grid item xs={4}>
                <Button
                  variant="outlined"
                  startIcon={<SaveIcon />}
                  onClick={() => setHoldBillModalOpen(true)}
                  disabled={cart.length === 0}
                  fullWidth
                  size="large"
                >
                  Hold Bill
                </Button>
              </Grid>
              <Grid item xs={4}>
              {(cart.length > 0 || customer) && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleClearDraft}
                    fullWidth
                    size="large"
                  >
                    Clear Draft
                  </Button>
              )}
              </Grid>
            </Grid>
          </Box>
        )}
          
          {/* Category navigation */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            borderBottom: 1, 
            borderColor: 'divider',
            px: 2
          }}>
            {selectedParentCategory !== 0 && (
              <Button 
                onClick={handleBackToParentCategories}
                startIcon={<ArrowBackIcon />}
                size="small"
                sx={{ mr: 2 }}
              >
                Back
              </Button>
            )}
            <Tabs
              value={selectedCategory}
              onChange={handleCategoryChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ flexGrow: 1 }}
            >
              {displayedCategories.map(category => (
                <Tab key={category.id} label={category.name} value={category.id} />
              ))}
            </Tabs>
          </Box>
          
          {isMobile && (
            <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider' }}>
              <TextField
                placeholder="Search products..."
                variant="outlined"
                size="small"
                fullWidth
                value={searchQuery}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          )}
        </StyledAppBar>
        
        {/* Main content */}
        <Box sx={{ 
          display: 'flex', 
          flexGrow: 1,
          overflow: 'hidden',
          position: 'relative'
        }}>
          {/* Products grid - shown in both desktop and mobile */}
          <Box sx={{ 
            flexGrow: 1, 
            overflow: 'auto',
          pr: { md: `${cartWidth}px` },
            width: '100%',
            height: '100%',
            pt: 2,
            // Add custom scrollbar styles to the Box container as well
            '&::-webkit-scrollbar': {
              width: '10px',
              backgroundColor: 'rgba(0, 0, 0, 0.05)'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '5px',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.3)'
              }
            },
            // For Firefox
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(0, 0, 0, 0.2) rgba(0, 0, 0, 0.05)'
          }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                <CircularProgress />
              </Box>
            ) : (
              <POSProductList 
                products={filteredProducts} 
                onAddToCart={handleAddToCart}
              />
            )}
          </Box>
          
          {/* Cart panel - only visible on desktop */}
          {!isMobile && (
            <Box sx={{ 
            width: cartWidth, 
              flexShrink: 0,
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              borderLeft: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              zIndex: 10,
              overflow: 'hidden',
              display: { xs: 'none', md: 'block' }
            }}>
                          <POSCart 
              cartItems={cart}
              onUpdateQuantity={handleUpdateQuantity}
              onRemoveItem={handleRemoveFromCart}
              onCheckout={handleOpenPayment}
              onClearCart={handleClearCart}
              onUpdatePrice={handleUpdatePrice}
              totals={calculateTotals()}
              discount={discount}
              onDiscountChange={setDiscount}
              additionalCharges={additionalCharges}
              onAdditionalChargesChange={setAdditionalCharges}
            />
            </Box>
          )}
        </Box>
        
        {/* Mobile Cart Drawer */}
        <Drawer
          anchor="right"
          open={cartOpen}
          onClose={() => setCartOpen(false)}
          sx={{ display: { xs: 'block', md: 'none' } }}
        PaperProps={{ sx: { width: '90%', maxWidth: cartWidth } }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6">
              Shopping Cart
            </Typography>
            <IconButton onClick={() => setCartOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <POSCart 
            cartItems={cart}
            onUpdateQuantity={handleUpdateQuantity}
            onRemoveItem={handleRemoveFromCart}
            onCheckout={() => {
              setCartOpen(false);
              handleOpenPayment();
            }}
            onClearCart={handleClearCart}
            onUpdatePrice={handleUpdatePrice}
            totals={calculateTotals()}
            discount={discount}
            onDiscountChange={setDiscount}
            additionalCharges={additionalCharges}
            onAdditionalChargesChange={setAdditionalCharges}
          />
        </Drawer>
        
        {/* Customer Dialog */}
        <Dialog 
          open={customerDialogOpen} 
          onClose={() => setCustomerDialogOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            Select Customer
            <IconButton
              aria-label="close"
              onClick={() => setCustomerDialogOpen(false)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <CustomerSelector 
            onSelectCustomer={(selectedCustomer) => {
              setCustomer(selectedCustomer);
                setCustomerDialogOpen(false);
              }}
            />
          </DialogContent>
        </Dialog>
      
      {/* Recent Transactions Dialog */}
      <Dialog
        open={recentTransactionsOpen}
        onClose={() => setRecentTransactionsOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Recent Transactions
          <IconButton
            aria-label="close"
            onClick={() => setRecentTransactionsOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loadingTransactions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {recentTransactions.length === 0 ? (
                <Typography align="center" color="textSecondary" sx={{ py: 3 }}>
                  No recent transactions found
                </Typography>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Order #</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Customer</TableCell>
                        <TableCell>Items</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentTransactions.map(transaction => (
                        <TableRow key={transaction.id}>
                          <TableCell>{transaction.orderNumber || transaction.id}</TableCell>
                          <TableCell>{new Date(transaction.orderDate).toLocaleString()}</TableCell>
                          <TableCell>{transaction.customer?.name || 'Walk-in Customer'}</TableCell>
                          <TableCell>{transaction.orderItems?.length || 0} items</TableCell>
                          <TableCell align="right">${transaction.totalAmount?.toFixed(2)}</TableCell>
                          <TableCell>
                            <Chip 
                              label={transaction.status || 'Completed'} 
                              color={transaction.status === 'PAID' ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
          </DialogContent>
        </Dialog>
        
        {/* Payment Dialog */}
        <Dialog
          open={paymentDialogOpen}
          onClose={() => !processingPayment && setPaymentDialogOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            Complete Payment
            <IconButton
              aria-label="close"
              onClick={() => !processingPayment && setPaymentDialogOpen(false)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
              }}
              disabled={processingPayment}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {errorMessage && (
              <Alert 
                severity="error" 
                onClose={handleErrorClose}
                sx={{ mb: 2 }}
              >
                {errorMessage}
              </Alert>
            )}
            
            {successMessage && (
              <Alert 
                severity="success" 
                onClose={handleSuccessClose}
                sx={{ mb: 2 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={() => handlePrintReceipt(['CUSTOMER'])}
                  >
                    Print Receipt
                  </Button>
                }
              >
                {successMessage}
              </Alert>
            )}
            
            <POSPayment 
              totals={calculateTotals()}
              onCompletePayment={handleCompleteSale}
              customer={customer}
              loading={processingPayment}
              saleCompleted={!!currentSale}
              onPrintReceipt={handlePrintReceipt}
            />
          </DialogContent>
        </Dialog>

        {/* Hold Bill Modal */}
        <HoldBillModal
          open={holdBillModalOpen}
          onClose={() => setHoldBillModalOpen(false)}
          customer={customer}
          cartItems={cart}
          onSuccess={handleHoldBillSuccess}
        />

        {/* View Hold Bills Modal */}
        <ViewHoldBillsModal
          open={viewHoldBillsModalOpen}
          onClose={() => setViewHoldBillsModalOpen(false)}
          onProcessBill={handleProcessHoldBill}
        />
      </Box>
  );
};

export default POS; 